import { useState } from 'react'

import { CloseButton, Divider, Loader, Text } from '@mantine/core'
import dayjs from 'dayjs'

import { useMutation, useQuery } from '@redwoodjs/web'

import DnDrawer from 'src/components/Core/DnDrawer'
import { toast } from 'src/components/Overlays/Toast/Toast'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { ExpirationTrackerStatus } from 'src/graphql/types/expirationTrackers'
import {
  GET_EXPIRATION_TRACKER,
  UPDATE_EXPIRATION_TRACKER,
  CANCEL_EXPIRATION_NOTIFICATION,
  RESCHEDULE_EXPIRATION_NOTIFICATION,
  ADD_EXPIRATION_NOTIFICATION,
  SYNC_EXPIRATION_NOTIFICATIONS,
  RENEW_EXPIRATION_TRACKER,
  CANCEL_EXPIRATION_TRACKER,
  ARCHIVE_EXPIRATION_TRACKER,
} from 'src/graphql/expirationTrackers.graphql'

import AuditTrailSection from './drawer/AuditTrailSection'
import DocumentSection from './drawer/DocumentSection'
import NotificationsSection from './drawer/NotificationsSection'
import OverviewSection from './drawer/OverviewSection'

interface ExpirationDetailDrawerProps {
  opened: boolean
  onClose: () => void
  trackerId: string | null
  onTrackerUpdated?: () => void
  onOpenApplicant?: (applicantId: string, metaKey?: boolean) => void
}

const ExpirationDetailDrawer = ({
  opened,
  onClose,
  trackerId,
  onTrackerUpdated,
  onOpenApplicant,
}: ExpirationDetailDrawerProps) => {
  const { data, loading, refetch } = useQuery(GET_EXPIRATION_TRACKER, {
    variables: { expirationTrackerId: trackerId },
    skip: !trackerId,
  })

  const tracker = data?.expirationTracker

  const [renewTracker] = useMutation(RENEW_EXPIRATION_TRACKER, {
    onCompleted: () => {
      toast('Tracker marked as renewed', 'success')
      refetch()
      onTrackerUpdated?.()
    },
    onError: () => toast('Failed to renew tracker', 'error'),
  })

  const [cancelTracker] = useMutation(CANCEL_EXPIRATION_TRACKER, {
    onCompleted: () => {
      toast('Tracker cancelled', 'success')
      refetch()
      onTrackerUpdated?.()
    },
    onError: () => toast('Failed to cancel tracker', 'error'),
  })

  const [archiveTracker] = useMutation(ARCHIVE_EXPIRATION_TRACKER, {
    onCompleted: () => {
      toast('Tracker archived', 'success')
      refetch()
      onTrackerUpdated?.()
    },
    onError: () => toast('Failed to archive tracker', 'error'),
  })

  const [cancelNotification] = useMutation(CANCEL_EXPIRATION_NOTIFICATION, {
    onCompleted: () => {
      toast('Notification cancelled', 'success')
      refetch()
      onTrackerUpdated?.()
    },
    onError: () => toast('Failed to cancel notification', 'error'),
  })

  const [updateTracker] = useMutation(UPDATE_EXPIRATION_TRACKER, {
    onCompleted: () => {
      toast('Tracker updated', 'success')
      refetch()
      onTrackerUpdated?.()
    },
    onError: () => toast('Failed to update tracker', 'error'),
  })

  const [rescheduleNotification] = useMutation(
    RESCHEDULE_EXPIRATION_NOTIFICATION,
    {
      onCompleted: () => {
        toast('Notification rescheduled', 'success')
        refetch()
        onTrackerUpdated?.()
      },
      onError: () => toast('Failed to reschedule notification', 'error'),
    }
  )

  const [addNotification] = useMutation(ADD_EXPIRATION_NOTIFICATION, {
    onCompleted: () => {
      toast('Notification added', 'success')
      refetch()
      onTrackerUpdated?.()
    },
    onError: () => toast('Failed to add notification', 'error'),
  })

  const [syncNotifications] = useMutation(SYNC_EXPIRATION_NOTIFICATIONS, {
    onCompleted: () => {
      toast('Notifications synced with config', 'success')
      refetch()
      onTrackerUpdated?.()
    },
    onError: (error) =>
      toast(error?.message || 'Failed to sync notifications', 'error'),
  })

  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)

  // Confirmation state
  const [confirmAction, setConfirmAction] = useState<{
    title: string
    description: string
    onConfirm: () => void
  } | null>(null)

  const withConfirm = (
    title: string,
    description: string,
    action: () => void
  ) => {
    setConfirmAction({ title, description, onConfirm: action })
  }

  const handleUpdateExpirationDate = (newDate: Date) => {
    if (!trackerId) return

    const scheduledNotifs = tracker?.notifications?.filter(
      (n) => n.status === 'SCHEDULED'
    )
    const wouldCancelNotifs = scheduledNotifs?.some((n) => {
      const offsetMs = n.offsetDays * 24 * 60 * 60 * 1000
      const newScheduledDate = new Date(newDate.getTime() - offsetMs)
      return dayjs(newScheduledDate).startOf('day').isBefore(dayjs().startOf('day'))
    })

    const doUpdate = () =>
      updateTracker({
        variables: {
          expirationTrackerId: trackerId,
          input: { expirationDate: newDate.toISOString() },
        },
      })

    if (wouldCancelNotifs) {
      withConfirm(
        'Update Expiration Date',
        'Changing this date will cancel scheduled notifications that would fall in the past.',
        doUpdate
      )
    } else {
      doUpdate()
    }
  }

  const handleRescheduleNotification = (
    expirationNotificationId: string,
    newScheduledDate: Date
  ) => {
    rescheduleNotification({
      variables: {
        expirationNotificationId,
        newScheduledDate: newScheduledDate.toISOString(),
      },
    })
  }

  const handleRenew = () => {
    if (!trackerId) return
    withConfirm(
      'Mark as Renewed',
      'This will mark the tracker as renewed and cancel all scheduled notifications.',
      () => renewTracker({ variables: { expirationTrackerId: trackerId } })
    )
  }

  const handleCancel = () => {
    if (!trackerId) return
    withConfirm(
      'Cancel Tracker',
      'This will cancel the tracker and all scheduled notifications. This action cannot be undone.',
      () => cancelTracker({ variables: { expirationTrackerId: trackerId } })
    )
  }

  const handleArchive = () => {
    if (!trackerId) return
    withConfirm(
      'Archive Tracker',
      'This will archive the tracker and cancel all scheduled notifications.',
      () => archiveTracker({ variables: { expirationTrackerId: trackerId } })
    )
  }

  const handleCancelNotification = (expirationNotificationId: string) => {
    withConfirm(
      'Cancel Notification',
      'This will cancel the scheduled notification.',
      () => cancelNotification({ variables: { expirationNotificationId } })
    )
  }

  const handleAddNotification = (input: {
    offsetDays: number
    scheduledDate: string
    actionType: string
    recipient: string
  }) => {
    if (!trackerId) return
    addNotification({
      variables: { expirationTrackerId: trackerId, input },
    })
  }

  const handleSyncNotifications = () => {
    if (!trackerId) return
    withConfirm(
      'Sync with config',
      "This adds any notifications missing from the current schedule for this tracker. Existing notifications aren't changed or removed.",
      () =>
        syncNotifications({
          variables: { expirationTrackerId: trackerId },
        })
    )
  }

  const handleStatusChange = (newStatus: string) => {
    if (!trackerId) return
    switch (newStatus) {
      case ExpirationTrackerStatus.RENEWED:
        handleRenew()
        break
      case ExpirationTrackerStatus.CANCELLED:
        handleCancel()
        break
      case ExpirationTrackerStatus.ARCHIVED:
        handleArchive()
        break
    }
  }

  return (
    <>
      <DnDrawer opened={opened} onClose={onClose} size="27vw" stackId="expiration-detail-drawer">
        {loading || !tracker ? (
          <div className="flex h-full items-center justify-center">
            <Loader size="sm" color="gray" />
          </div>
        ) : (
          <div className="flex h-screen flex-col space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <Text
                size="lg"
                fw={500}
                className="text-doubleNickel-gray-900"
              >
                {tracker.label}
              </Text>
              <CloseButton onClick={onClose} />
            </div>

            <Divider variant="dashed" />

            {/* Body */}
            <div className="flex min-h-0 flex-1 flex-col space-y-4 overflow-auto pb-10">
              <OverviewSection
                tracker={tracker}
                onUpdateExpirationDate={
                  tracker.status === ExpirationTrackerStatus.ACTIVE
                    ? handleUpdateExpirationDate
                    : undefined
                }
                onStatusChange={handleStatusChange}
                isUpdatingStatus={isUpdatingStatus}
                onOpenApplicant={onOpenApplicant}
              />

              <Divider variant="dashed" />

              <DocumentSection tracker={tracker} />

              <Divider variant="dashed" />

              <NotificationsSection
                notifications={tracker.notifications}
                onCancelNotification={handleCancelNotification}
                onRescheduleNotification={handleRescheduleNotification}
                onAddNotification={
                  tracker.status === ExpirationTrackerStatus.ACTIVE
                    ? handleAddNotification
                    : undefined
                }
                onSyncNotifications={
                  tracker.status === ExpirationTrackerStatus.ACTIVE
                    ? handleSyncNotifications
                    : undefined
                }
                trackerExpirationDate={tracker.expirationDate}
              />

              <Divider variant="dashed" />

              <AuditTrailSection auditTrails={tracker.auditTrails} />
            </div>
          </div>
        )}
      </DnDrawer>

      <DeleteModal
        title={confirmAction?.title}
        description={confirmAction?.description}
        opened={!!confirmAction}
        close={() => setConfirmAction(null)}
        stackId="expiration-delete-modal"
        onDelete={async () => {
          confirmAction?.onConfirm()
        }}
      />
    </>
  )
}

export default ExpirationDetailDrawer
